import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  photoTypes as types,
  LoadPropertyPhotosAction,
  LoadPropertyPhotosSuccessAction,
  LoadPropertyPhotosFailAction,
  UpdatePropertyPhotoAction,
  UpdatePropertyPhotoSuccessAction,
  UpdatePropertyPhotoFailAction,
  SetPropertyPhotoAsPrimaryAction,
  SetPropertyPhotoAsPrimarySuccessAction,
  SetPropertyPhotoAsPrimaryFailAction,
  DownloadPropertyPhotoAction,
  DownloadPropertyPhotoSuccessAction,
  DownloadPropertyPhotoFailAction,
  DeletePropertyPhotoAction,
  DeletePropertyPhotoSuccessAction,
  DeletePropertyPhotoFailAction,
  BulkAssignPropertyPhotosToComponentAction,
  BulkAssignPropertyPhotosToComponentSuccessAction,
  BulkAssignPropertyPhotosToComponentFailAction,
  BulkDownloadPropertyPhotosAction,
  BulkDownloadPropertyPhotosSuccessAction,
  BulkDownloadPropertyPhotosFailAction,
  BulkDeletePropertyPhotosAction,
  BulkDeletePropertyPhotosSuccessAction,
  BulkDeletePropertyPhotosFailAction,
  BulkUpdatePropertyPhotosReportSettingsAction,
  BulkUpdatePropertyPhotosReportSettingsSuccessAction,
  BulkUpdatePropertyPhotosReportSettingsFailAction,
  BulkAddPhotosToProjectAction,
  BulkAddPhotosToProjectSuccessAction,
  BulkAddPhotosToProjectFailAction,
  RemovePhotoFromProjectAction,
  RemovePhotoFromProjectSuccessAction,
  RemovePhotoFromProjectFailAction,
} from './photo.actions';
import * as FileSaver from 'file-saver-es';
import { PhotoService } from '../shared/services/photo.service';
import { BlobWithFilename } from '../../../shared';
import { catchError, filter, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { PropertyPhoto } from '../shared/models';
import * as ProjectActions from '../../projects/projects-store/projects.actions';
import { getProject } from 'app/internal/projects/projects-store/projects.selectors';
import { ToastrService } from 'ngx-toastr';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducers';

@Injectable()
export class PhotoEffects {

  constructor(
    private actions$: Actions,
    private photoService: PhotoService,
    private store: Store<AppState>,
    private toastr: ToastrService
  ) { }

  getPropertyPhotos$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.LOAD_PROPERTY_PHOTOS),
      map(action => (action as LoadPropertyPhotosAction).payload),
      mergeMap((payload) =>
        this.photoService
          .getByProperty(payload.propertyId)
          .pipe(
            map((response: PropertyPhoto[]) => new LoadPropertyPhotosSuccessAction(response)),
            catchError((error) => of(new LoadPropertyPhotosFailAction(error)))
          )
      )
    );
  });

  updatePropertyPhoto$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.UPDATE_PROPERTY_PHOTO),
      map(action => (action as UpdatePropertyPhotoAction).payload),
      mergeMap((payload) =>
        this.photoService
          .updatePropertyPhoto(payload)
          .pipe(
            map((response: PropertyPhoto) => new UpdatePropertyPhotoSuccessAction(response)),
            catchError((error) => of(new UpdatePropertyPhotoFailAction(error)))
          )
      )
    );
  });

  /**
   * When a photo is updated successfully, refresh the current project if we're in Project context
   * (i.e. a project is loaded and the updated photo is in that project's projectPhotos).
   * No-op when on Component photos or Property-level Photos area.
   */
  refreshProjectOnPhotoUpdateSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.UPDATE_PROPERTY_PHOTO_SUCCESS),
      map(action => (action as UpdatePropertyPhotoSuccessAction).payload),
      withLatestFrom(this.store.select(getProject)),
      filter(([updatedPhoto, project]) => {
        if (!project?.projectPhotos?.length) {
          return false;
        }
        const isPhotoInCurrentProject = project.projectPhotos.some(
          p => p.propertyPhotoId === updatedPhoto.propertyPhotoId
        );
        return isPhotoInCurrentProject;
      }),
      mergeMap(([, project]) => [
        ProjectActions.projectCrudActions.loadProject({ payload: project.projectId }),
      ])
    );
  });

  setPropertyPhotoAsPrimary$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.SET_PROPERTY_PHOTO_AS_PRIMARY),
      map(action => (action as SetPropertyPhotoAsPrimaryAction).payload),
      mergeMap((payload) =>
        this.photoService
          .setAsPrimaryPhoto(payload.propertyComponentId, payload.propertyPhotoId)
          .pipe(
            map((response: any) => new SetPropertyPhotoAsPrimarySuccessAction(response)),
            catchError((error) => of(new SetPropertyPhotoAsPrimaryFailAction(error)))
          )
      )
    );
  });

  downloadPropertyPhoto$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.DOWNLOAD_PROPERTY_PHOTO),
      map(action => (action as DownloadPropertyPhotoAction).payload),
      mergeMap((propertyPhotoId: string) =>
        this.photoService.downloadPropertyPhoto(propertyPhotoId)
          .pipe(
            map((response: BlobWithFilename) => new DownloadPropertyPhotoSuccessAction(response)),
            catchError((error) => of(new DownloadPropertyPhotoFailAction(error)))
          )
      )
    );
  });

  downloadPropertyPhotoSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.DOWNLOAD_PROPERTY_PHOTO_SUCCESS),
      map(action => (action as DownloadPropertyPhotoSuccessAction).payload),
      tap((blob: BlobWithFilename) => { FileSaver.saveAs(blob, blob.filename); }),
    );
  });

  deletePropertyPhoto$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.DELETE_PROPERTY_PHOTO),
      map(action => (action as DeletePropertyPhotoAction).payload),
      mergeMap((photoId) =>
        this.photoService
          .deletePropertyPhoto(photoId)
          .pipe(
            map((response) => new DeletePropertyPhotoSuccessAction(response)),
            catchError((error) => of(new DeletePropertyPhotoFailAction(error)))
          )
      )
    );
  });

  bulkAssignPhotosToComponent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.BULK_ASSIGN_PROPERTY_PHOTOS_TO_COMPONENT),
      map(action => (action as BulkAssignPropertyPhotosToComponentAction).payload),
      mergeMap((payload) =>
        this.photoService
          .bulkAssignPropertyPhotosToComponent(payload)
          .pipe(
            map((response: any) => new BulkAssignPropertyPhotosToComponentSuccessAction(response)),
            catchError((error) => of(new BulkAssignPropertyPhotosToComponentFailAction(error)))
          )
      )
    );
  });

  bulkUpdatePhotosReportSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.BULK_UPDATE_PROPERTY_PHOTOS_REPORT_SETTINGS),
      map(action => (action as BulkUpdatePropertyPhotosReportSettingsAction).payload),
      mergeMap((payload) =>
        this.photoService
          .bulkUpdatePropertyPhotosReportSettings(payload)
          .pipe(
            map((response: any) => new BulkUpdatePropertyPhotosReportSettingsSuccessAction(response)),
            catchError((error) => of(new BulkUpdatePropertyPhotosReportSettingsFailAction(error)))
          )
      )
    );
  });

  bulkDownloadPropertyPhotos$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.BULK_DOWNLOAD_PROPERTY_PHOTOS),
      map(action => (action as BulkDownloadPropertyPhotosAction).payload),
      mergeMap((payload) =>
        this.photoService
          .bulkDownloadPropertyPhotos(payload)
          .pipe(
            map((response: BlobWithFilename) => new BulkDownloadPropertyPhotosSuccessAction(response)),
            catchError((error) => of(new BulkDownloadPropertyPhotosFailAction(error)))
          )
      )
    );
  });

  bulkDownloadPropertyPhotoSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.BULK_DOWNLOAD_PROPERTY_PHOTOS_SUCCESS),
      map(action => (action as BulkDownloadPropertyPhotosSuccessAction).payload),
      tap((blob: BlobWithFilename) => { FileSaver.saveAs(blob, blob.filename); }),
    );
  });

  bulkDeletePropertyPhotos$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.BULK_DELETE_PROPERTY_PHOTOS),
      map(action => (action as BulkDeletePropertyPhotosAction).payload),
      mergeMap((payload) =>
        this.photoService
          .bulkDeletePropertyPhotos(payload)
          .pipe(
            map((response: any) => new BulkDeletePropertyPhotosSuccessAction(response)),
            catchError((error) => of(new BulkDeletePropertyPhotosFailAction(error)))
          )
      )
    );
  });

  addPhotoToProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.BULK_ADD_PHOTOS_TO_PROJECT),
      map(action => (action as BulkAddPhotosToProjectAction).payload),
      mergeMap((payload) =>
        this.photoService
          .bulkAddPhotosToProject(payload)
          .pipe(
            map((response: any) => new BulkAddPhotosToProjectSuccessAction(response)),
            catchError((error) => of(new BulkAddPhotosToProjectFailAction(error)))
          )
      )
    );
  });

  /**
   * When photos are added to project successfully, refresh the current project so the card view updates.
   * Only runs when we have a current project loaded (project context).
   */
  refreshProjectOnAddPhotosToProjectSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.BULK_ADD_PHOTOS_TO_PROJECT_SUCCESS),
      withLatestFrom(this.store.select(getProject)),
      filter(([, project]) => project != null),
      mergeMap(([, project]) => [
        ProjectActions.projectCrudActions.loadProject({ payload: project.projectId }),
      ])
    );
  });

  removePhotoFromProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.REMOVE_PHOTO_FROM_PROJECT),
      map(action => (action as RemovePhotoFromProjectAction).payload),
      mergeMap((payload) =>
        this.photoService
          .removePhotoFromProject(payload.projectId, payload.propertyPhotoId)
          .pipe(
            map((response: any) => new RemovePhotoFromProjectSuccessAction(response)),
            catchError((error) => of(new RemovePhotoFromProjectFailAction(error)))
          )
      )
    );
  });

  removePhotoFromProjectSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.REMOVE_PHOTO_FROM_PROJECT_SUCCESS),
      map(action => (action as RemovePhotoFromProjectSuccessAction).payload),
      withLatestFrom(
        this.store.select(getProject)
      ),
      tap(() => {
        this.toastr.success('Project Photo deleted');
      }),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      mergeMap(([payload, project]) => [
        ProjectActions.projectCrudActions.loadProject({ payload: project.projectId }),
      ])
    );
  });

}
